import { ShoppingCartOutlined } from '@ant-design/icons';
import type { TableColumnsType, UploadFile, UploadProps } from 'antd';
import {
  DatePicker,
  Dropdown,
  Image,
  Menu,
  Popover,
  Skeleton,
  Spin,
  Tooltip,
  message,
} from 'antd';
import vi from 'antd/es/date-picker/locale/vi_VN';
import * as dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { createRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import orderApi from '../../apis/order';
import userApi from '../../apis/user';
import {
  OrderStatusColorMap,
  OrderStatusLabelMap,
  OrderTypeLabelMap,
  PlatformLabelMap,
  ProductSizeMap,
  ProductTypeLabelMap,
  ShipmentStatus,
  TrackingFilter,
} from '../../constants/order';
import { useHasMounted } from '../../hooks';
import {
  IOrder,
  OrderStatus,
  ProductSize,
  ProductType,
  ResendRequestStatusLabelMap,
  SortOption,
} from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { IUser } from '../../interfaces/UserModel';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { CustomTable, FlexContainer, PageContainer } from '../../styles/Global';
import DesignDownloadService, {
  DesignWithOrderContext,
  DownloadProgress,
  OrderFilesContext,
  VariantSummary,
} from '../../utils/designDownloadService';
import BulkTrackingUpdateModal from '../Modal/BulkTrackingUpdateModal';
import DownloadProgressModal from '../Modal/DownloadProgressModal';
import PageHeader from '../PageHeader/PageHeader';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import {
  BulkActionButton,
  CustomSize,
  NestedTable,
  NestedTableImage,
  SearchFilterContainer,
  SellerDateWrapper,
  SpinContainer,
  StatusTag,
  StickySummaryButton,
  StyledQuantity,
  StyledSearch,
  StyledSelect,
  SummaryTable,
  SummaryTableContainer,
} from './Order.styled';

const OrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [itemsPagination, setItemsPagintion] = useState<
    PaginationParamsResponse<IOrder>
  >({
    items: [],
    page: 1,
    perPage: 10,
    total: 0,
    pageCount: 0,
  });
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const isAdmin =
    currentUser?.role === Role.ADMIN || currentUser?.role === Role.EMPLOYEE;
  const [spinLoading, setSpinLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [shipmentStatus, setShipmentStatus] = useState<ShipmentStatus>();
  const [country, setCountry] = useState('');
  const [sortFieldValue, setSortFieldValue] = useState('');
  const [sortOrderValue, setSortOrderValue] = useState('');
  const [trackingFilter, setTrackingFilter] = useState<TrackingFilter>();
  const [filterDate, setFilterDate] = useState<{
    startDate: any;
    endDate: any;
  }>();
  const [search, setSearch] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sellers, setSellers] = useState<Array<IUser>>();
  const [sellerUsername, setSellerUsername] = useState<Array<string>>();
  const mounted = useHasMounted();
  const downloadLinkRef = createRef<HTMLAnchorElement>();

  // Download progress state
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>(
    []
  );
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [variantSummary, setVariantSummary] = useState<VariantSummary[]>([]);

  // Bulk tracking update modal state
  const [bulkTrackingModalVisible, setBulkTrackingModalVisible] =
    useState(false);

  useEffect(() => {
    if (!mounted()) return;

    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    searchOrder();
  }, [
    mounted,
    status,
    country,
    search,
    filterDate,
    sortFieldValue,
    sortOrderValue,
    trackingFilter,
    sellerUsername,
    shipmentStatus,
  ]);

  useEffect(() => {
    if (!mounted()) return;

    searchOrder();
  }, [mounted, currentPage, perPage]);

  useEffect(() => {
    if (!mounted()) return;

    if (isAdmin) {
      getSellers();
    }
  }, [mounted, isAdmin]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled:
        record.status === OrderStatus.CANCELLED ||
        record.status === OrderStatus.REFUNDED,
    }),
  };

  const deleteOrder = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      setActionLoading(true);
      await orderApi.deleteOrder([id]);
      message.success('Delete order success!');
      // update list
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp.splice(idx, 1);
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Delete order fail!');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const quickActionHandle = async (id: string, status: string) => {
    try {
      setActionLoading(true);
      await orderApi.updateOrderStatus(id, status);
      message.success('Update order status success!');

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx].status = status as OrderStatus;
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Update order status fail!');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const sellerCancelOrder = async (id: string) => {
    try {
      setActionLoading(true);
      await orderApi.sellerCancelOrder(id);

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx].status = status as OrderStatus;
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Seller cancel order fail!');
    }
  };

  const adminRefundOrder = async (id: string) => {
    try {
      setActionLoading(true);
      await orderApi.adminRefundOrder(id);

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx].status = status as OrderStatus;
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Admin refund order fail!');
    }
  };

  const getSellers = async () => {
    try {
      const sellers = await userApi.listUsers({ all: true });
      setSellers(sellers.items);
    } catch (e) {
      message.error('Get sellers fail!');
      console.error(e);
    }
  };

  const expandedRowRender = (row: any) => {
    // Create consolidated data structure - one row per order design item
    const consolidatedData: any[] = [];

    if (row.orderDesigns) {
      row.orderDesigns.forEach((orderDesign: any, designIndex: number) => {
        if (orderDesign.placements && orderDesign.placements.length > 0) {
          // New structure: consolidate all placements into one row
          const frontPlacements = orderDesign.placements.filter(
            (p: any) => p.side === 'front'
          );
          const backPlacements = orderDesign.placements.filter(
            (p: any) => p.side === 'back'
          );

          // Get all unique designs from placements
          const allDesigns = orderDesign.placements
            .map((p: any) => p.design)
            .filter((design: any) => design); // Remove null/undefined

          // Get unique design URLs for display
          const designUrls = allDesigns
            .map(
              (design: any) =>
                design?.thumbnailUrl ??
                design?.originalFileUrl ??
                design?.designUrl
            )
            .filter((url: string) => url)
            .filter(
              (url: string, index: number, array: string[]) =>
                array.indexOf(url) === index // Remove duplicates
            );

          // Get mockup URLs from all placements
          const mockupUrls = orderDesign.placements
            .map((p: any) => p.mockupUrl)
            .filter((url: string) => url);

          consolidatedData.push({
            key: designIndex,
            design: allDesigns[0], // Keep first design for backward compatibility
            designs: allDesigns,
            designUrls,
            quantity: orderDesign.quantity,
            productTemplateVariant: orderDesign.productTemplateVariant,
            itemCost: orderDesign.itemCost,
            placements: orderDesign.placements,
            frontPlacements,
            backPlacements,
            mockupUrls,
            isConsolidated: true,
          });
        } else {
          // Legacy structure: backward compatibility
          const legacyDesign = (orderDesign as any)?.design;
          const legacyDesignUrl = legacyDesign
            ? legacyDesign.thumbnailUrl ??
              legacyDesign.originalFileUrl ??
              legacyDesign.designUrl
            : null;

          consolidatedData.push({
            key: designIndex,
            design: legacyDesign,
            designUrls: legacyDesignUrl ? [legacyDesignUrl] : [],
            quantity: orderDesign.quantity,
            productTemplateVariant: orderDesign.productTemplateVariant,
            itemCost: orderDesign.itemCost,
            mockupUrl: orderDesign.mockupUrl,
            isConsolidated: false,
          });
        }
      });
    }

    const columns: TableColumnsType<any> = [
      {
        title: 'Ảnh thiết kế',
        dataIndex: 'design',
        key: 'designUrls',
        render: (_: any, item: any) => {
          // Handle both consolidated and legacy cases with designUrls
          if (item.designUrls?.length > 0) {
            if (item.designUrls.length === 1) {
              // Single design - show larger
              const url = item.designUrls[0];
              return item?.design?.isGeneratingThumbnail ? (
                <Skeleton.Image active style={{ width: 80, height: 80 }} />
              ) : (
                <NestedTableImage
                  src={url}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
              );
            } else {
              // Multiple designs - show grid
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.designUrls
                    .slice(0, 3)
                    .map((url: string, idx: number) => (
                      <Image
                        key={idx}
                        src={url}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                    ))}
                  {item.designUrls.length > 3 && (
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#666',
                        border: '1px solid #d9d9d9',
                      }}
                    >
                      +{item.designUrls.length - 3}
                    </div>
                  )}
                </div>
              );
            }
          }

          return (
            <span style={{ color: '#999', fontSize: '12px' }}>Không có</span>
          );
        },
        width: '15%',
      },
      {
        title: 'Số lượng',
        dataIndex: 'quantity',
        key: 'quantity',
        render: (_: any, item: any) => (
          <StyledQuantity $moreThan1={item?.quantity! > 1}>
            {item?.quantity}
          </StyledQuantity>
        ),
        width: '8%',
      },
      {
        title: 'Kích thước',
        dataIndex: 'size',
        key: 'size',
        render: (_: any, item: any) => (
          <CustomSize
            $2in={item?.productTemplateVariant?.size === ProductSize.S}
            $3in={item?.productTemplateVariant?.size === ProductSize.M}
            $4in={item?.productTemplateVariant?.size === ProductSize.L}
            $5in={item?.productTemplateVariant?.size === ProductSize.XL}
            $6in={item?.productTemplateVariant?.size === ProductSize.XXL}
            $card={item?.productTemplateVariant?.size === ProductSize.XXXL}
          >
            {item?.productTemplateVariant?.size}
          </CustomSize>
        ),
        width: '8%',
      },
      {
        title: 'Vị trí thiết kế',
        key: 'placements',
        render: (_: any, item: any) => {
          if (item.isConsolidated) {
            return (
              <div style={{ fontSize: '12px' }}>
                {/* Front placements */}
                {item.frontPlacements.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div
                      style={{
                        color: '#1890ff',
                        fontWeight: 'bold',
                        marginBottom: '4px',
                      }}
                    >
                      🔵 Mặt trước ({item.frontPlacements.length})
                    </div>
                    {item.frontPlacements.map((placement: any, idx: number) => (
                      <div
                        key={`front-${idx}`}
                        style={{
                          marginLeft: '12px',
                          color: '#666',
                          fontSize: '11px',
                        }}
                      >
                        • {placement.printAreaId}
                        {placement.mockupUrl && (
                          <span style={{ color: '#52c41a', marginLeft: '4px' }}>
                            ✓
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Back placements */}
                {item.backPlacements.length > 0 && (
                  <div>
                    <div
                      style={{
                        color: '#52c41a',
                        fontWeight: 'bold',
                        marginBottom: '4px',
                      }}
                    >
                      🟢 Mặt sau ({item.backPlacements.length})
                    </div>
                    {item.backPlacements.map((placement: any, idx: number) => (
                      <div
                        key={`back-${idx}`}
                        style={{
                          marginLeft: '12px',
                          color: '#666',
                          fontSize: '11px',
                        }}
                      >
                        • {placement.printAreaId}
                        {placement.mockupUrl && (
                          <span style={{ color: '#52c41a', marginLeft: '4px' }}>
                            ✓
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* No placements */}
                {item.frontPlacements.length === 0 &&
                  item.backPlacements.length === 0 && (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                      Không có vị trí
                    </span>
                  )}
              </div>
            );
          }
          return (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Legacy</span>
          );
        },
        width: '20%',
      },
      {
        title: 'Giá',
        dataIndex: 'itemCost',
        key: 'price',
        render: (_: any, item: any) => (
          <p>{item?.itemCost?.toLocaleString('vi-VN')} VNĐ</p>
        ),
        width: '12%',
      },
      {
        title: 'Mockup',
        key: 'mockupUrls',
        render: (_: any, item: any) => {
          if (item.mockupUrls?.length > 0) {
            if (item.mockupUrls.length === 1) {
              // Single mockup - show larger
              return (
                <Image
                  src={item.mockupUrls[0]}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
              );
            } else {
              // Multiple mockups - show grid
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.mockupUrls
                    .slice(0, 3)
                    .map((url: string, idx: number) => (
                      <Image
                        key={idx}
                        src={url}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                    ))}
                  {item.mockupUrls.length > 3 && (
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#666',
                        border: '1px solid #d9d9d9',
                      }}
                    >
                      +{item.mockupUrls.length - 3}
                    </div>
                  )}
                </div>
              );
            }
          }

          // Legacy single mockup
          if (item.mockupUrl) {
            return (
              <Image
                src={item.mockupUrl}
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                }}
              />
            );
          }

          return (
            <span style={{ color: '#999', fontSize: '12px' }}>Không có</span>
          );
        },
        width: '15%',
      },
    ];

    return (
      <NestedTable
        className="nested-table"
        columns={columns as any}
        dataSource={consolidatedData}
        pagination={false}
        showHeader={true}
        bordered
      />
    );
  };

  const columns = [
    {
      title: isAdmin ? 'Người bán/Ngày' : 'Ngày',
      key: 'createdAt',
      editable: false,
      dataIndex: 'createdAt',
      sorter: true,
      showSorterTooltip: false,
      fixed: 'left',
      render: (_: any, order: IOrder) => (
        <Link to={`/order/${order?.id}/edit`}>
          <SellerDateWrapper>
            {isAdmin && <p>{order?.seller?.username}</p>}
            <p>{dayjs.default(order?.createdAt).format('DD MMM YYYY')}</p>
          </SellerDateWrapper>
        </Link>
      ),
      width: '12%',
    },
    {
      title: 'Mã đơn hàng',
      key: 'orderId',
      editable: false,
      dataIndex: 'orderId',
      fixed: 'left',
      render: (_: any, order: IOrder) => (
        <Tooltip title={order?.orderId}>
          <div className="ellipsis" style={{ maxWidth: 160 }}>
            <Link to={`/order/${order?.id}/edit`}>
              <p>{order?.orderId}</p>
            </Link>
          </div>
        </Tooltip>
      ),
      width: '12%',
    },
    {
      title: 'Sản phẩm',
      key: 'products',
      editable: false,
      dataIndex: 'products',
      width: '16%',
      render: (_: any, order: IOrder) => {
        const formatProductInfo = (orderDesign: any) => {
          const quantity = orderDesign?.quantity || 0;
          const color =
            orderDesign?.productTemplateVariant?.productTemplate?.color ||
            orderDesign?.productTemplate?.color ||
            'N/A';
          const size =
            ProductSizeMap[orderDesign?.productTemplateVariant?.size] || 'N/A';
          const form =
            orderDesign?.productTemplateVariant?.productTemplate?.form || 'N/A';

          // Add placement count for new structure
          if (orderDesign?.placements && orderDesign.placements.length > 0) {
            const placementCount = orderDesign.placements.length;
            return `${quantity} ${form} ${color} ${size} (${placementCount} vị trí)`;
          }

          return `${quantity} ${color} ${size}`;
        };

        const getAllMockups = (orderDesign: any) => {
          // Get all mockups for both front and back
          if (orderDesign?.placements && orderDesign.placements.length > 0) {
            const frontMockups = orderDesign.placements
              .filter((p: any) => p.side === 'front')
              .map((p: any) => ({
                url:
                  p.mockupUrl ||
                  p.design?.thumbnailUrl ||
                  p.design?.originalFileUrl ||
                  p.design?.designUrl,
                side: 'front',
                printArea: p.printAreaId,
              }))
              .filter((item: any) => item.url);

            const backMockups = orderDesign.placements
              .filter((p: any) => p.side === 'back')
              .map((p: any) => ({
                url:
                  p.mockupUrl ||
                  p.design?.thumbnailUrl ||
                  p.design?.originalFileUrl ||
                  p.design?.designUrl,
                side: 'back',
                printArea: p.printAreaId,
              }))
              .filter((item: any) => item.url);

            return { front: frontMockups, back: backMockups };
          }

          // Legacy structure
          const legacyUrl =
            orderDesign?.mockupUrl ||
            orderDesign?.design?.mockupUrl ||
            orderDesign?.design?.thumbnailUrl ||
            orderDesign?.design?.originalFileUrl ||
            orderDesign?.design?.designUrl;

          return legacyUrl
            ? {
                front: [
                  { url: legacyUrl, side: 'legacy', printArea: 'legacy' },
                ],
                back: [],
              }
            : { front: [], back: [] };
        };

        // Generate product summary with visual colors
        const generateProductSummary = (orderDesigns: any[]) => {
          // Color mapping to hex codes for visual display
          const colorHexMap: { [key: string]: string } = {
            // English colors
            black: '#000000',
            white: '#FFFFFF',
            red: '#FF0000',
            blue: '#0066CC',
            green: '#00AA00',
            yellow: '#FFDD00',
            orange: '#FF6600',
            purple: '#800080',
            pink: '#FF69B4',
            gray: '#808080',
            grey: '#808080',
            brown: '#A0522D',
            navy: '#000080',
            maroon: '#800000',
            olive: '#808000',
            lime: '#00FF00',
            aqua: '#00FFFF',
            teal: '#008080',
            silver: '#C0C0C0',
            gold: '#FFD700',
            // Vietnamese colors
            đen: '#000000',
            trắng: '#FFFFFF',
            đỏ: '#FF0000',
            'xanh dương': '#0066CC',
            'xanh lá': '#00AA00',
            vàng: '#FFDD00',
            cam: '#FF6600',
            tím: '#800080',
            hồng: '#FF69B4',
            xám: '#808080',
            nâu: '#A0522D',
            'xanh navy': '#000080',
            // Common variations
            'dark blue': '#003366',
            'light blue': '#87CEEB',
            'dark green': '#006600',
            'light green': '#90EE90',
            'dark gray': '#505050',
            'light gray': '#D3D3D3',
          };

          const getColorHex = (color: string): string => {
            const normalizedColor = color.toLowerCase().trim();
            return colorHexMap[normalizedColor] || '#CCCCCC'; // Default gray
          };

          const summaryMap = new Map<
            string,
            { hex: string; sizes: Map<string, number> }
          >();

          orderDesigns.forEach((od) => {
            const originalColor =
              od?.productTemplateVariant?.productTemplate?.color ||
              od?.productTemplate?.color ||
              'Unknown';
            const colorHex = getColorHex(originalColor);
            const size = od?.productTemplateVariant?.size || 'Unknown';
            const quantity = od?.quantity || 0;

            if (!summaryMap.has(originalColor)) {
              summaryMap.set(originalColor, {
                hex: colorHex,
                sizes: new Map(),
              });
            }

            const colorData = summaryMap.get(originalColor)!;
            const currentQty = colorData.sizes.get(size) || 0;
            colorData.sizes.set(size, currentQty + quantity);
          });

          return summaryMap;
        };

        const productSummaryData = generateProductSummary(
          order.orderDesigns || []
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Product Summary with Visual Colors */}
            <div
              style={{
                padding: '6px 8px',
                borderRadius: '4px',
                backgroundColor: '#e6f7ff',
                border: '1px solid #91d5ff',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {Array.from(productSummaryData.entries()).map(
                ([colorName, colorData], index) => {
                  const sizeParts: string[] = [];
                  colorData.sizes.forEach((qty, size) => {
                    sizeParts.push(`${qty} ${ProductSizeMap[size]}`);
                  });

                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {/* Color dot */}
                      <div
                        style={{
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          backgroundColor: colorData.hex,
                          border:
                            colorData.hex === '#FFFFFF'
                              ? '1px solid #ddd'
                              : 'none',
                          flexShrink: 0,
                        }}
                      />
                      {/* Size summary */}
                      <span style={{ color: '#0958d9' }}>
                        {sizeParts.join(' ')}
                      </span>
                    </div>
                  );
                }
              )}
            </div>

            {/* Individual Product Details */}
            {order.orderDesigns?.map((od, idx) => {
              const productText = formatProductInfo(od);
              const allMockups = getAllMockups(od);
              const hasAnyMockups =
                allMockups.front.length > 0 || allMockups.back.length > 0;

              return (
                <Tooltip
                  overlayStyle={{
                    maxWidth: '700px',
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                  key={idx}
                  title={
                    hasAnyMockups ? (
                      <div style={{ padding: '8px' }}>
                        {/* Side by side layout for front and back */}
                        <div
                          style={{
                            display: 'flex',
                            gap: '16px',
                            justifyContent: 'center',
                          }}
                        >
                          {/* Front Mockups */}
                          {allMockups.front.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              {allMockups.front.map(
                                (mockup: any, frontIdx: number) => (
                                  <div
                                    key={`front-${frontIdx}`}
                                    style={{
                                      textAlign: 'center',
                                      backgroundColor: '#ffffff',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      border: '2px solid #1890ff',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#1890ff',
                                        fontWeight: 'bold',
                                        marginBottom: '4px',
                                      }}
                                    >
                                      🔵 Mặt trước
                                    </div>
                                    <Image
                                      src={mockup.url}
                                      alt={`Front mockup ${frontIdx + 1}`}
                                      preview={false}
                                      width={250}
                                      height={250}
                                      style={{
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                      }}
                                    />
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        marginTop: '4px',
                                      }}
                                    >
                                      {mockup.printArea}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          )}

                          {/* Back Mockups */}
                          {allMockups.back.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              {allMockups.back.map(
                                (mockup: any, backIdx: number) => (
                                  <div
                                    key={`back-${backIdx}`}
                                    style={{
                                      textAlign: 'center',
                                      backgroundColor: '#ffffff',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      border: '2px solid #52c41a',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#52c41a',
                                        fontWeight: 'bold',
                                        marginBottom: '4px',
                                      }}
                                    >
                                      🟢 Mặt sau
                                    </div>
                                    <Image
                                      src={mockup.url}
                                      alt={`Back mockup ${backIdx + 1}`}
                                      preview={false}
                                      width={250}
                                      height={250}
                                      style={{
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                      }}
                                    />
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        marginTop: '4px',
                                      }}
                                    >
                                      {mockup.printArea}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      'Không có mockup'
                    )
                  }
                  placement="right"
                >
                  <div
                    style={{
                      cursor: 'pointer',
                      padding: '2px 4px',
                      fontSize: '12px',
                      borderRadius: '4px',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {productText}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
    {
      title: 'SL',
      key: 'quantity',
      editable: false,
      dataIndex: 'quantity',
      width: '4%',
      render: (_: any, order: IOrder) => (
        <p>
          {order.orderDesigns?.reduce((acc, od) => acc + od?.quantity, 0) || 0}
        </p>
      ),
    },
    {
      title: 'Nền tảng',
      key: 'platform',
      editable: false,
      dataIndex: 'platform',
      width: '10%',
      render: (_: any, order: IOrder) => (
        <p>{PlatformLabelMap[order?.platform]}</p>
      ),
    },
    {
      title: 'Loại đơn',
      key: 'orderType',
      editable: false,
      dataIndex: 'orderType',
      width: '10%',
      render: (_: any, order: IOrder) => (
        <p>{OrderTypeLabelMap[order?.orderType]}</p>
      ),
    },
    {
      title: 'Ghi chú',
      key: 'notes',
      editable: false,
      dataIndex: 'notes',
      width: '15%',
    },
    {
      title: 'Tổng tiền',
      key: 'totalCost',
      editable: false,
      dataIndex: 'totalCost',
      sorter: true,
      showSorterTooltip: false,
      width: '10%',
      render: (_: any, order: IOrder) => (
        <p>
          {order.status === OrderStatus.REFUNDED
            ? '0'
            : order?.totalCost?.toLocaleString('vi-VN')}{' '}
          VNĐ
        </p>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      editable: false,
      dataIndex: 'status',
      width: '11%',
      render: (_: any, order: IOrder) => {
        return !!order?.resendRequest ? (
          <FlexContainer $direction="column">
            <StatusTag
              $color={OrderStatusColorMap[order?.status].color}
              $backgroundColor={
                OrderStatusColorMap[order?.status].backgroundColor
              }
              $borderColor={OrderStatusColorMap[order?.status].borderColor}
            >
              {order?.status}
            </StatusTag>
            <StatusTag
              $color={OrderStatusColorMap[order?.resendRequest?.status].color}
              $backgroundColor={
                OrderStatusColorMap[order?.resendRequest?.status]
                  .backgroundColor
              }
              $borderColor={
                OrderStatusColorMap[order?.resendRequest?.status].borderColor
              }
              $fontSize={10}
              className="m__t--4"
            >
              {ResendRequestStatusLabelMap[order?.resendRequest?.status]}
            </StatusTag>
          </FlexContainer>
        ) : (
          <StatusTag
            $color={OrderStatusColorMap[order?.status].color}
            $backgroundColor={
              OrderStatusColorMap[order?.status].backgroundColor
            }
            $borderColor={OrderStatusColorMap[order?.status].borderColor}
          >
            {OrderStatusLabelMap[order?.status]}
          </StatusTag>
        );
      },
    },
    {
      title: 'Mã vận đơn',
      key: 'trackingCode',
      editable: false,
      dataIndex: 'trackingCode',
      width: '10%',
      render: (_: any, order: IOrder) => {
        if (!order?.trackingCode) {
          return (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa có</span>
          );
        }
        return (
          <Tooltip title={`Tracking: ${order.trackingCode}`}>
            <span
              style={{
                color: '#52c41a',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              {order.trackingCode.length > 15
                ? `${order.trackingCode.substring(0, 15)}...`
                : order.trackingCode}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Mã vận chuyển',
      key: 'trackingReference',
      editable: false,
      dataIndex: 'trackingReference',
      width: '12%',
      render: (_: any, order: IOrder) => {
        if (!order?.trackingReference) {
          return (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa có</span>
          );
        }
        return (
          <Tooltip title="Xem mã vận chuyển">
            <a
              href={order.trackingReference}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1890ff',
                textDecoration: 'none',
                fontSize: '12px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              Xem mã VC
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      editable: false,
      dataIndex: 'id',
      width: '8%',
      render: (_: any, order: any) =>
        order.status === OrderStatus.CANCELLED ||
        order.status === OrderStatus.REFUNDED ? null : (
          <Dropdown
            disabled={actionLoading}
            trigger={['hover']}
            overlay={menuQuickAction(order)}
          >
            <button
              type="button"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              Hành động nhanh
            </button>
          </Dropdown>
        ),

      // hideForUser: true,
    },
  ];

  const menuQuickAction = (order: IOrder) => (
    <Menu style={{ marginBottom: '12px' }}>
      {isAdmin && order.status === OrderStatus.PENDING && (
        <Menu.Item
          key="process"
          onClick={() =>
            order.id &&
            quickActionHandle(order.id, OrderStatus.PRODUCTION_IN_PROGRESS)
          }
        >
          Chuyển sang đang xử lý
        </Menu.Item>
      )}
      {isAdmin && order.status === OrderStatus.PRODUCTION_IN_PROGRESS && (
        <Menu.Item
          key="process"
          onClick={() =>
            order.id && quickActionHandle(order.id, OrderStatus.PROCESSED)
          }
        >
          Chuyển sang đã xử lý
        </Menu.Item>
      )}
      {isAdmin && order.status === OrderStatus.PROCESSED && (
        <Menu.Item
          key="process"
          onClick={() =>
            order.id && quickActionHandle(order.id, OrderStatus.SHIPPING)
          }
        >
          Chuyển sang đang giao hàng
        </Menu.Item>
      )}
      {isAdmin &&
        !!order.status &&
        [OrderStatus.PRODUCTION_IN_PROGRESS, OrderStatus.SHIPPING].includes(
          order.status as OrderStatus
        ) && (
          <Menu.Item
            key="finish"
            onClick={() =>
              order.id && quickActionHandle(order.id, OrderStatus.SHIPPED)
            }
          >
            Chuyển sang đã giao hàng
          </Menu.Item>
        )}
      {isAdmin && order.status !== OrderStatus.REFUNDED && (
        <Menu.Item
          key="cancel"
          onClick={() => order.id && adminRefundOrder(order.id)}
        >
          Refund
        </Menu.Item>
      )}
      {!!order.trackingCode && (
        <Menu.Item key="copy-tracking">
          <span
            onClick={() => {
              navigator.clipboard.writeText(order.trackingCode);
              message.success('Đã sao chép tracking code!');
            }}
            style={{ cursor: 'pointer' }}
          >
            Sao chép tracking code
          </span>
        </Menu.Item>
      )}
      {!!order.trackingReference && (
        <Menu.Item key="view-label">
          <a
            href={order.trackingReference}
            target="_blank"
            rel="noopener noreferrer"
          >
            Xem nhãn vận chuyển
          </a>
        </Menu.Item>
      )}
      <Menu.Item key="edit">
        <Link to={`/order/${order?.id}/edit`}>Sửa</Link>
      </Menu.Item>
      {order.status === OrderStatus.PENDING && (
        <Menu.Item
          key="delete"
          onClick={() => order.id && sellerCancelOrder(order.id)}
        >
          Huỷ
        </Menu.Item>
      )}
    </Menu>
  );

  const menuBulkAction = () => (
    <Menu style={{ marginBottom: '12px' }}>
      <Menu.Item
        key="pending"
        onClick={() => bulkActionHandle(OrderStatus.PENDING)}
      >
        Chuyển sang đang chờ
      </Menu.Item>
      <Menu.Item
        key="process"
        onClick={() => bulkActionHandle(OrderStatus.PRODUCTION_IN_PROGRESS)}
      >
        Chuyển sang đang xử lý
      </Menu.Item>
      <Menu.Item
        key="reshipped"
        onClick={() => bulkActionHandle(OrderStatus.SHIPPING)}
      >
        Chuyển sang đang giao hàng lại
      </Menu.Item>
      <Menu.Item
        key="shipped"
        onClick={() => bulkActionHandle(OrderStatus.SHIPPED)}
      >
        Chuyển sang đã giao hàng
      </Menu.Item>
      <Menu.Item
        key="cancel"
        onClick={() => bulkActionHandle(OrderStatus.REFUNDED)}
      >
        Chuyển sang đã hủy
      </Menu.Item>
      <Menu.Divider />
      <Menu.SubMenu key="download" title="Tải xuống" disabled={downloadLoading}>
        <Menu.Item
          key="download-all"
          onClick={() => handleBulkDownload('all')}
          disabled={downloadLoading}
        >
          Tất cả (thiết kế + tracking)
        </Menu.Item>
        <Menu.Item
          key="download-designs"
          onClick={() => handleBulkDownload('designs')}
          disabled={downloadLoading}
        >
          Chỉ thiết kế
        </Menu.Item>
        <Menu.Item
          key="download-trackings"
          onClick={() => handleBulkDownload('trackings')}
          disabled={downloadLoading}
        >
          Chỉ tracking
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  const bulkActionHandle = async (status = OrderStatus.PENDING) => {
    if (!status || selectedRowKeys.length === 0) {
      return;
    }

    try {
      setBulkActionLoading(true);
      await orderApi.bulkUpdateOrderStatus(selectedRowKeys as string[], status);

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      selectedRowKeys.forEach((id) => {
        const idx = tmp.findIndex((t) => t.id === id);
        if (idx > -1) {
          tmp[idx].status = status;
        }
      });
      setItemsPagintion({
        ...itemsPagination,
        items: tmp,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Helper function to collect designs from orders
  const collectDesignsFromOrders = (
    orders: IOrder[]
  ): DesignWithOrderContext[] => {
    const allDesigns: DesignWithOrderContext[] = [];

    orders.forEach((order) => {
      if (order.orderDesigns) {
        order.orderDesigns.forEach((orderDesign, index) => {
          // Handle new placement-based structure
          if (orderDesign.placements && orderDesign.placements.length > 0) {
            // New structure: iterate through placements to get designs
            orderDesign.placements.forEach((placement, placementIndex) => {
              const designObj = placement.design;
              if (
                designObj &&
                typeof designObj === 'object' &&
                'designUrl' in designObj &&
                designObj.designUrl
              ) {
                // Create multiple copies based on quantity
                const quantity = orderDesign?.quantity || 1;

                for (let copyIndex = 1; copyIndex <= quantity; copyIndex++) {
                  // Enhanced context with placement info and copy number
                  const designWithContext = {
                    id: designObj.id,
                    sku: designObj.sku || `design_${designObj.id}`,
                    designUrl: designObj.designUrl,
                    originalFileUrl: designObj.originalFileUrl,
                    seller: designObj.seller || '',
                    createdAt: designObj.createdAt || new Date(),
                    updatedAt: designObj.updatedAt || new Date(),
                    // Add order context for filename generation
                    orderId: order.orderId || order.id,
                    orderIndex: index + 1, // 1-based indexing
                    // Add placement context
                    placementIndex: placementIndex + 1,
                    side: placement.side || 'front',
                    printArea: placement.printAreaId || 'unknown',
                    // Add copy number for quantity-based naming
                    copyNumber: copyIndex,
                    // Add variant information for summary
                    productTemplateName:
                      orderDesign?.productTemplate?.name ||
                      orderDesign?.productTemplateVariant?.productTemplate
                        ?.name ||
                      'T-Shirt',
                    productColor:
                      orderDesign?.productTemplate?.color ||
                      orderDesign?.productTemplateVariant?.productTemplate
                        ?.color ||
                      'Mixed',
                    productSize:
                      orderDesign?.productTemplateVariant?.size || 'One Size',
                    quantity: 1, // Each copy represents 1 item
                  } as DesignWithOrderContext & {
                    placementIndex: number;
                    side: string;
                    printArea: string;
                    copyNumber: number;
                  };

                  allDesigns.push(designWithContext);
                }
              }
            });
          } else {
            // Legacy structure: handle backward compatibility
            const designObj = (orderDesign as any)?.design;
            if (
              designObj &&
              typeof designObj === 'object' &&
              'designUrl' in designObj &&
              designObj.designUrl
            ) {
              // Create multiple copies based on quantity for legacy structure too
              const quantity = (orderDesign as any)?.quantity || 1;

              for (let copyIndex = 1; copyIndex <= quantity; copyIndex++) {
                allDesigns.push({
                  id: designObj.id,
                  sku: designObj.sku || `design_${designObj.id}`,
                  designUrl: designObj.designUrl,
                  originalFileUrl: designObj.originalFileUrl,
                  seller: designObj.seller || '',
                  createdAt: designObj.createdAt || new Date(),
                  updatedAt: designObj.updatedAt || new Date(),
                  // Add order context for filename generation
                  orderId: order.orderId || order.id,
                  orderIndex: index + 1, // 1-based indexing
                  // Add copy number for quantity-based naming
                  copyNumber: copyIndex,
                  // Add variant information for summary
                  productTemplateName:
                    (orderDesign as any)?.productTemplateVariant
                      ?.productTemplate?.name ||
                    (orderDesign as any)?.productTemplate?.name ||
                    'T-Shirt',
                  productColor:
                    (orderDesign as any)?.productTemplateVariant
                      ?.productTemplate?.color ||
                    (orderDesign as any)?.productTemplate?.color ||
                    'Mixed',
                  productSize:
                    (orderDesign as any)?.productTemplateVariant?.size ||
                    'One Size',
                  quantity: 1, // Each copy represents 1 item
                } as DesignWithOrderContext & { copyNumber: number });
              }
            }
          }
        });
      }
    });

    return allDesigns;
  };

  // Handle bulk download with different types: 'all', 'designs', 'trackings'
  const handleBulkDownload = async (type: 'all' | 'designs' | 'trackings') => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn đơn hàng để tải xuống');
      return;
    }

    const selectedOrders = itemsPagination.items.filter((order) =>
      selectedRowKeys.includes(order.id)
    );

    if (type === 'all') {
      // Original behavior: download all files organized by order folders
      const orderFiles: OrderFilesContext[] = [];
      selectedOrders.forEach((order) => {
        const orderDesigns = collectDesignsFromOrders([order]);

        // Only add orders that have designs or tracking labels
        if (orderDesigns.length > 0 || order.trackingReference) {
          orderFiles.push({
            orderId: order.orderId || order.id,
            designs: orderDesigns,
            trackingLabelUrl: order.trackingReference || undefined,
          });
        }
      });

      if (orderFiles.length === 0) {
        message.warning(
          'Không tìm thấy thiết kế hoặc nhãn vận chuyển trong các đơn hàng đã chọn'
        );
        return;
      }

      // Calculate total files for validation
      const totalFiles = orderFiles.reduce((total, order) => {
        return total + order.designs.length + (order.trackingLabelUrl ? 1 : 0);
      }, 0);

      if (totalFiles === 0) {
        message.warning('Không có tệp nào để tải xuống');
        return;
      }

      // Generate variant summary
      const generatedVariantSummary =
        DesignDownloadService.generateVariantSummary(orderFiles);
      setVariantSummary(generatedVariantSummary);

      // Show progress modal
      setDownloadModalVisible(true);
      setDownloadProgress([]);
      setDownloadLoading(true);

      const zipFileName = `orders_${new Date().toISOString().slice(0, 10)}.zip`;

      try {
        await DesignDownloadService.downloadOrderFilesAsZip(
          orderFiles,
          zipFileName,
          {
            onProgress: (progress) => {
              setDownloadProgress((prev) => {
                const existing = prev.find(
                  (p) => p.fileName === progress.fileName
                );
                if (existing) {
                  return prev.map((p) =>
                    p.fileName === progress.fileName ? progress : p
                  );
                } else {
                  return [...prev, progress];
                }
              });
            },
            onError: (error, fileName) => {
              console.error(`Download error for ${fileName}:`, error);
              message.error(
                `Không thể tải xuống ${fileName}: ${error.message}`
              );
            },
            maxFileSize: 50, // 50MB per file
            timeout: 30000, // 30 seconds
          }
        );

        const totalDownloaded = orderFiles.reduce((total, order) => {
          return (
            total + order.designs.length + (order.trackingLabelUrl ? 1 : 0)
          );
        }, 0);
        message.success(
          `Đã tải xuống thành công ${totalDownloaded} tệp từ ${orderFiles.length} đơn hàng!`
        );

        // Clear selection after successful download
        setSelectedRowKeys([]);
      } catch (error) {
        console.error('Download failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Lỗi không xác định';
        message.error(`Tải xuống thất bại: ${errorMessage}`);
      } finally {
        setDownloadLoading(false);
      }
    } else if (type === 'designs') {
      // Download all designs in a single folder (no per-order folders)
      const allDesigns = collectDesignsFromOrders(selectedOrders);

      if (allDesigns.length === 0) {
        message.warning('Không tìm thấy thiết kế trong các đơn hàng đã chọn');
        return;
      }

      // Generate variant summary from designs
      const orderFilesForSummary: OrderFilesContext[] = selectedOrders.map(
        (order) => ({
          orderId: order.orderId || order.id,
          designs: collectDesignsFromOrders([order]),
        })
      );
      const generatedVariantSummary =
        DesignDownloadService.generateVariantSummary(orderFilesForSummary);
      setVariantSummary(generatedVariantSummary);

      // Show progress modal
      setDownloadModalVisible(true);
      setDownloadProgress([]);
      setDownloadLoading(true);

      const zipFileName = `designs_${new Date()
        .toISOString()
        .slice(0, 10)}.zip`;

      try {
        await DesignDownloadService.downloadDesignsAsZip(
          allDesigns,
          zipFileName,
          {
            onProgress: (progress) => {
              setDownloadProgress((prev) => {
                const existing = prev.find(
                  (p) => p.fileName === progress.fileName
                );
                if (existing) {
                  return prev.map((p) =>
                    p.fileName === progress.fileName ? progress : p
                  );
                } else {
                  return [...prev, progress];
                }
              });
            },
            onError: (error, fileName) => {
              console.error(`Download error for ${fileName}:`, error);
              message.error(
                `Không thể tải xuống ${fileName}: ${error.message}`
              );
            },
            maxFileSize: 50, // 50MB per file
            timeout: 30000, // 30 seconds
          }
        );

        message.success(
          `Đã tải xuống thành công ${allDesigns.length} thiết kế!`
        );

        // Clear selection after successful download
        setSelectedRowKeys([]);
      } catch (error) {
        console.error('Download failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Lỗi không xác định';
        message.error(`Tải xuống thất bại: ${errorMessage}`);
      } finally {
        setDownloadLoading(false);
      }
    } else if (type === 'trackings') {
      // Download all tracking labels in a single folder (no per-order folders)
      const trackingLabels: Array<{
        url: string;
        orderId: string;
      }> = [];

      selectedOrders.forEach((order) => {
        if (order.trackingReference) {
          trackingLabels.push({
            url: order.trackingReference,
            orderId: order.orderId || order.id,
          });
        }
      });

      if (trackingLabels.length === 0) {
        message.warning(
          'Không tìm thấy nhãn vận chuyển trong các đơn hàng đã chọn'
        );
        return;
      }

      // Show progress modal
      setDownloadModalVisible(true);
      setDownloadProgress([]);
      setDownloadLoading(true);
      setVariantSummary([]); // No variant summary for trackings only

      const zipFileName = `trackings_${new Date()
        .toISOString()
        .slice(0, 10)}.zip`;

      try {
        await DesignDownloadService.downloadTrackingLabelsAsZip(
          trackingLabels,
          zipFileName,
          {
            onProgress: (progress) => {
              setDownloadProgress((prev) => {
                const existing = prev.find(
                  (p) => p.fileName === progress.fileName
                );
                if (existing) {
                  return prev.map((p) =>
                    p.fileName === progress.fileName ? progress : p
                  );
                } else {
                  return [...prev, progress];
                }
              });
            },
            onError: (error, fileName) => {
              console.error(`Download error for ${fileName}:`, error);
              message.error(
                `Không thể tải xuống ${fileName}: ${error.message}`
              );
            },
            maxFileSize: 50, // 50MB per file
            timeout: 30000, // 30 seconds
          }
        );

        message.success(
          `Đã tải xuống thành công ${trackingLabels.length} nhãn vận chuyển!`
        );

        // Clear selection after successful download
        setSelectedRowKeys([]);
      } catch (error) {
        console.error('Download failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Lỗi không xác định';
        message.error(`Tải xuống thất bại: ${errorMessage}`);
      } finally {
        setDownloadLoading(false);
      }
    }
  };

  // const bulkActionResendRequestHandle = async (status: RequestStatus) => {
  //   if (!status || selectedRowKeys.length === 0) {
  //     return;
  //   }

  //   try {
  //     setBulkActionLoading(true);
  //     await orderApi.bulkUpdateResendRequestStatus(
  //       resendRequestOrdersSelected.map(
  //         (order) => order.resendRequest?.id || ''
  //       ),
  //       status
  //     );

  //     // Update list order
  //     const tmp = cloneDeep(itemsPagination.items);
  //     resendRequestOrdersSelected.forEach((order) => {
  //       const idx = tmp.findIndex((t) => t.id === order.id);
  //       if (idx > -1) {
  //         if (!!tmp[idx].resendRequest) {
  //           tmp[idx].resendRequest!.status = status;
  //         }
  //       }
  //     });
  //     setItemsPagintion({
  //       ...itemsPagination,
  //       items: tmp,
  //     });
  //   } catch (e) {
  //     console.error(e);
  //   } finally {
  //     setBulkActionLoading(false);
  //   }
  // };

  const searchOrder = async () => {
    try {
      setSpinLoading(true);
      let query = {
        country,
        status,
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
        tracking: trackingFilter,
        seller: sellerUsername,
        shipmentStatus,
      };
      if (sortFieldValue) {
        query[sortFieldValue] = sortOrderValue;
      }
      if (search) {
        (query as any).search = search;
      }

      const res = await orderApi.getOrders(query, {
        page: currentPage,
        perPage,
      });
      setItemsPagintion(res);
    } catch (error) {
      setItemsPagintion({
        items: [],
        page: 1,
        perPage,
        total: 0,
        pageCount: 0,
      });
      console.error(error);
    } finally {
      setSpinLoading(false);
    }
  };

  const onStateChanges = async (page: number, perPage: number) => {
    navigate(`${location.pathname}?page=${page}&perPage=${perPage}`, {
      replace: true,
    });
  };

  const onPageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPerPage(pageSize);
    onStateChanges(page, pageSize);
  };

  const handleChange = (_pagination: any, _filters: any, sorter: any) => {
    // handle sort
    if (sorter) {
      if (sorter.column) {
        let sorterField = sorter.field;
        switch (sorter.field) {
          case 'createdAt':
            sorterField = 'byDate';
            break;
          case 'totalCost':
            sorterField = 'byTotalCost';
            break;
          case 'trackingReference':
            sorterField = 'byTracking';
            break;
        }
        const sorterOrder =
          sorter.order &&
          (sorter.order === 'ascend' ? SortOption.ASC : SortOption.DESC);
        setSortFieldValue(sorterField);
        setSortOrderValue(sorterOrder);
      } else {
        setSortFieldValue('');
        setSortOrderValue('');
      }
    }

    if (_filters) {
      const trackingValue = _filters['trackingReference']?.[0];
      setTrackingFilter(trackingValue ? trackingValue : undefined);

      const shipmentStatusValue = _filters['shipmentStatus']?.[0];
      setShipmentStatus(shipmentStatusValue ? shipmentStatusValue : undefined);
    }
  };

  const handleExportExcel = async () => {
    try {
      setSpinLoading(true);
      let query = {
        country,
        status,
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
        tracking: trackingFilter,
        seller: sellerUsername,
        shipmentStatus,
      };

      if (sortFieldValue) {
        query[sortFieldValue] = sortOrderValue;
      }

      if (search) {
        (query as any).search = search;
      }

      const csvData = await orderApi.exportOrdersCsv(query, {
        page: currentPage,
        perPage: perPage,
      });

      // Convert CSV data to Excel
      const workbook = XLSX.read(csvData, { type: 'string' });
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const tmpLink = document.createElement('a');
      tmpLink.href = url;
      const fileName = `${new Date().toDateString()}-report.xlsx`;
      tmpLink.setAttribute('download', fileName);
      document.body.appendChild(tmpLink);
      tmpLink.click();
      tmpLink.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      message.error('Sorry an error occurred while processing your request!');
    } finally {
      setSpinLoading(false);
    }
  };

  const handleOpenCsvFile: UploadProps['onChange'] = (file: any) => {
    setFileList(
      file?.fileList.length
        ? [
            {
              ...file.fileList[0],
              status:
                file.fileList[0].status === 'error'
                  ? 'done'
                  : file.fileList[0].status,
            },
          ]
        : file?.fileList
    );
  };

  const hanldeUploadOrders = async () => {
    try {
      setSpinLoading(true);

      const formData = new FormData();
      formData.append('dataFile', fileList[0]?.originFileObj || '');

      await orderApi.importOrderCsv(formData);
    } catch (error) {
      console.error(error);
      message.error('Sorry an error occurred while processing your file!');
    } finally {
      await searchOrder();
      setSpinLoading(false);
      setFileList([]);
    }
  };

  const handleBulkUpdateTracking = async () => {
    try {
      setSpinLoading(true);

      const formData = new FormData();
      formData.append('dataFile', fileList[0]?.originFileObj || '');

      await orderApi.bulkUpdateTracking(formData);
    } catch (error) {
      console.error(error);
      message.error('Sorry an error occurred while processing your file!');
    } finally {
      await searchOrder();
      setSpinLoading(false);
      setFileList([]);
    }
  };

  const handleBulkTrackingStatusUpdate = async (
    trackingCodes: string[],
    status: OrderStatus
  ) => {
    try {
      setBulkActionLoading(true);
      await orderApi.bulkUpdateOrderStatusByTrackingCode(trackingCodes, status);
      message.success(
        `Đã cập nhật thành công ${trackingCodes.length} đơn hàng sang ${OrderStatusLabelMap[status]}`
      );

      // Refresh the order list
      await searchOrder();
    } catch (error) {
      console.error(error);
      message.error('Cập nhật đơn hàng thất bại. Vui lòng thử lại.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Calculate product template summary from selected orders
  const calculateProductTemplateSummary = useMemo(() => {
    if (selectedRowKeys.length === 0) {
      return [];
    }

    const selectedOrders = itemsPagination.items.filter((order) =>
      selectedRowKeys.includes(order.id)
    );

    // Aggregate quantities by product template variant
    const summaryMap = new Map<
      string,
      {
        productTemplateId: string;
        productTemplateName: string;
        productType: ProductType;
        color: string;
        colorHexCode: string;
        form: string;
        variantId: string;
        size: ProductSize;
        quantity: number;
      }
    >();

    selectedOrders.forEach((order) => {
      order.orderDesigns?.forEach((orderDesign) => {
        const productTemplate =
          orderDesign.productTemplateVariant?.productTemplate;
        const variant = orderDesign.productTemplateVariant;

        if (!productTemplate || !variant) {
          return;
        }

        const key = `${productTemplate.id}_${variant.id}`;

        if (summaryMap.has(key)) {
          const existing = summaryMap.get(key)!;
          existing.quantity += orderDesign.quantity || 0;
        } else {
          summaryMap.set(key, {
            productTemplateId: productTemplate.id,
            productTemplateName: productTemplate.name || 'N/A',
            productType: productTemplate.type,
            color: productTemplate.color || 'N/A',
            colorHexCode: productTemplate.colorHexCode || '#f0f0f0',
            form: productTemplate.form || 'N/A',
            variantId: variant.id,
            size: variant.size,
            quantity: orderDesign.quantity || 0,
          });
        }
      });
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      // Sort by product template name, then by size
      if (a.productTemplateName !== b.productTemplateName) {
        return a.productTemplateName.localeCompare(b.productTemplateName);
      }
      return a.size.localeCompare(b.size);
    });
  }, [selectedRowKeys, itemsPagination.items]);

  // Summary table columns
  const summaryColumns: TableColumnsType<any> = [
    {
      title: 'Tên mẫu',
      dataIndex: 'productTemplateName',
      key: 'productTemplateName',
      width: '20%',
      render: (name: string) => <span style={{ fontWeight: 500 }}>{name}</span>,
    },
    {
      title: 'Loại',
      dataIndex: 'productType',
      key: 'productType',
      width: '10%',
      render: (type: ProductType) => (
        <span style={{ textTransform: 'uppercase', fontSize: '12px' }}>
          {ProductTypeLabelMap[type] || type}
        </span>
      ),
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      width: '15%',
      render: (color: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            className="color-indicator"
            style={{ backgroundColor: record.colorHexCode || '#f0f0f0' }}
          />
          <span>{color}</span>
        </div>
      ),
    },
    {
      title: 'Form',
      dataIndex: 'form',
      key: 'form',
      width: '15%',
    },
    {
      title: 'Kích thước',
      dataIndex: 'size',
      key: 'size',
      width: '15%',
      render: (size: ProductSize) => (
        <CustomSize
          $2in={size === ProductSize.S}
          $3in={size === ProductSize.M}
          $4in={size === ProductSize.L}
          $5in={size === ProductSize.XL}
          $6in={size === ProductSize.XXL}
          $card={size === ProductSize.XXXL}
        >
          {ProductSizeMap[size] || size}
        </CustomSize>
      ),
    },
    {
      title: 'Tổng số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '15%',
      align: 'right',
      render: (quantity: number) => (
        <span className="quantity-cell">
          {quantity.toLocaleString('vi-VN')}
        </span>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Quản lý đơn hàng"
        customBlock={
          <FlexContainer>
            {isAdmin && (
              <PrimaryButton
                className="font-weight--500 m__r--10"
                key="4"
                onClick={handleExportExcel}
              >
                Xuất Excel
              </PrimaryButton>
            )}
            {isAdmin && (
              <PrimaryButton
                className="font-weight--500 m__r--10"
                key="bulk-tracking"
                style={{
                  width: '100%',
                }}
                onClick={() => setBulkTrackingModalVisible(true)}
              >
                Cập nhật theo Excel
              </PrimaryButton>
            )}
            {isAdmin && (
              <BulkActionButton
                $disabled={bulkActionLoading || selectedRowKeys.length === 0}
                disabled={bulkActionLoading || selectedRowKeys.length === 0}
                trigger={['hover']}
                overlay={menuBulkAction()}
              >
                <span>Hành động nhanh</span>
              </BulkActionButton>
            )}
            {fileList.length > 0 && (
              <>
                <PrimaryButton
                  className="font-weight--500 m__r--10"
                  key="4"
                  onClick={handleBulkUpdateTracking}
                >
                  Cập nhật tracking
                </PrimaryButton>
                <PrimaryButton
                  className="font-weight--500 m__r--10"
                  key="4"
                  onClick={hanldeUploadOrders}
                >
                  Tải lên
                </PrimaryButton>
              </>
            )}
            {/* <Upload
              name="file"
              onChange={handleOpenCsvFile}
              multiple={false}
              fileList={fileList}
              accept=".csv"
            >
              {fileList.length < 1 && (
                <PrimaryButton className="font-weight--500 m__r--10" key="3">
                  Mở file CSV
                </PrimaryButton>
              )}
            </Upload> */}
            {/* <span ref={downloadLinkRef}>
              <PrimaryButton
                className="font-weight--500 m__r--10"
                key="2"
                onClick={handleExportExcel}
              >
                Xuất Excel
              </PrimaryButton>
            </span> */}
            {!isAdmin && (
              <PrimaryButton
                className="font-weight--500 w-100"
                key="3"
                onClick={() => {
                  navigate(`/order/create`);
                }}
              >
                + Tạo đơn hàng
              </PrimaryButton>
            )}
          </FlexContainer>
        }
      />
      <SearchFilterContainer $isAdmin={isAdmin}>
        <StyledSearch
          placeholder="Nhập để tìm kiếm..."
          onSearch={(val) => setSearch(val)}
        />
        <DatePicker.RangePicker
          onChange={(val) => {
            if (Array.isArray(val) && val.length > 1) {
              setFilterDate({
                startDate: dayjs.default(val[0]).toISOString(),
                endDate: dayjs.default(val[1]).toISOString(),
              });
            } else {
              setFilterDate({
                startDate: null,
                endDate: null,
              });
            }
          }}
          locale={vi}
          allowClear
        />
        {isAdmin && (
          <StyledSelect
            className="w-100"
            placeholder="Chọn người bán"
            // value={types}
            onChange={(val: any) => {
              setSellerUsername(val);
            }}
            options={sellers?.map((seller) => ({
              label: seller.username,
              value: seller.username,
            }))}
            mode="multiple"
          />
        )}
        <StyledSelect
          className="w-100"
          placeholder="Chọn trạng thái"
          // value={types}
          onChange={(val: any) => {
            setStatus(val);
          }}
          options={[
            { label: 'Tất cả trạng thái', value: '' },
            {
              label: OrderStatusLabelMap[OrderStatus.PENDING],
              value: OrderStatus.PENDING,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.PRODUCTION_IN_PROGRESS],
              value: OrderStatus.PRODUCTION_IN_PROGRESS,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.SHIPPING],
              value: OrderStatus.SHIPPING,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.SHIPPED],
              value: OrderStatus.SHIPPED,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.PROCESSED],
              value: OrderStatus.PROCESSED,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.REFUNDED],
              value: OrderStatus.REFUNDED,
            },
            {
              label: OrderStatusLabelMap[OrderStatus.CANCELLED],
              value: OrderStatus.CANCELLED,
            },
          ]}
        />
        {/* <StyledSelect
          className="w-100"
          placeholder="Lọc theo tracking"
          value={trackingFilter}
          onChange={(val: any) => {
            setTrackingFilter(val);
          }}
          options={[
            { label: 'Tất cả', value: undefined },
            {
              label: TrackingFilterLabelMap[TrackingFilter.NoTrackingCode],
              value: TrackingFilter.NoTrackingCode,
            },
            {
              label: TrackingFilterLabelMap[TrackingFilter.TrackingCodeOnly],
              value: TrackingFilter.TrackingCodeOnly,
            },
            {
              label: TrackingFilterLabelMap[TrackingFilter.NoTrackingReference],
              value: TrackingFilter.NoTrackingReference,
            },
            {
              label:
                TrackingFilterLabelMap[TrackingFilter.TrackingReferenceOnly],
              value: TrackingFilter.TrackingReferenceOnly,
            },
            {
              label: TrackingFilterLabelMap[TrackingFilter.NoTracking],
              value: TrackingFilter.NoTracking,
            },
            {
              label: TrackingFilterLabelMap[TrackingFilter.TrackingOnly],
              value: TrackingFilter.TrackingOnly,
            },
            {
              label: TrackingFilterLabelMap[TrackingFilter.ShipmentOnly],
              value: TrackingFilter.ShipmentOnly,
            },
          ]}
        /> */}
      </SearchFilterContainer>

      <CustomTable
        className="m__t--20"
        columns={columns as any}
        dataSource={itemsPagination.items}
        onChange={handleChange}
        pagination={{
          onChange: onPageChange,
          current: currentPage,
          total: itemsPagination.total,
          pageSize: itemsPagination.perPage,
          showSizeChanger: true,
          pageSizeOptions: [10, 50, 100],
        }}
        expandable={{
          expandedRowRender,
          defaultExpandAllRows: true,
          defaultExpandedRowKeys: ['0'],
        }}
        rowSelection={isAdmin ? rowSelection : undefined}
        rowKey={(record) => record?.id}
        bordered
      />
      {spinLoading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}

      {/* Download Progress Modal */}
      <DownloadProgressModal
        visible={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        progress={downloadProgress}
        estimatedSize={
          downloadProgress.length > 0
            ? DesignDownloadService.estimateDownloadSize(
                downloadProgress.map((p) => ({
                  id: p.fileName,
                  sku: p.fileName,
                  designUrl: '',
                  seller: '',
                  createdAt: new Date(),
                  updatedAt: new Date(),
                }))
              )
            : undefined
        }
        zipFileName={`orders_${new Date().toISOString().slice(0, 10)}.zip`}
        variantSummary={variantSummary}
      />

      {/* Bulk Tracking Update Modal */}
      <BulkTrackingUpdateModal
        visible={bulkTrackingModalVisible}
        onCancel={() => setBulkTrackingModalVisible(false)}
        onSubmit={handleBulkTrackingStatusUpdate}
        loading={bulkActionLoading}
      />

      {/* Sticky Summary Button with Popover */}
      {isAdmin && selectedRowKeys.length > 0 && (
        <Popover
          content={
            <SummaryTableContainer>
              <div className="summary-title">
                Tổng hợp sản phẩm ({selectedRowKeys.length} đơn hàng đã chọn)
              </div>
              {calculateProductTemplateSummary.length > 0 ? (
                <SummaryTable
                  columns={summaryColumns}
                  dataSource={calculateProductTemplateSummary}
                  pagination={false}
                  rowKey={(record) =>
                    `${record.productTemplateId}_${record.variantId}`
                  }
                  size="small"
                  bordered
                  scroll={{ x: 800, y: 400 }}
                />
              ) : (
                <div className="summary-empty">
                  Không có sản phẩm nào trong các đơn hàng đã chọn
                </div>
              )}
            </SummaryTableContainer>
          }
          title={null}
          trigger="hover"
          placement="topRight"
          overlayStyle={{ maxWidth: '900px', padding: 0 }}
          overlayClassName="summary-popover"
          overlayInnerStyle={{ padding: 0, borderRadius: '8px' }}
        >
          <StickySummaryButton $visible={selectedRowKeys.length > 0}>
            <ShoppingCartOutlined />
            {selectedRowKeys.length > 0 && (
              <span className="badge">{selectedRowKeys.length}</span>
            )}
          </StickySummaryButton>
        </Popover>
      )}
    </PageContainer>
  );
};

export default OrderList;
